import { useEffect, useMemo, useState } from "react";
import api, { API_BASE } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Plus, Package, Search, X, Check, Pencil, Trash2, FileUp } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../context/ConfirmContext";
import ImportDialog from "../components/ImportDialog";

const empty = { name: "", email: "", phone: "", street: "", city: "", zip: "", country: "DE", contact_person: "", lead_time_days: 7, our_customer_number: "", notes: "" };

export default function Suppliers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const confirm = useConfirm();
  const [importOpen, setImportOpen] = useState(false);

  // Product assignment state
  const [productSupplier, setProductSupplier] = useState(null); // supplier row currently open
  const [assignedItems, setAssignedItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerSelected, setPickerSelected] = useState({}); // item_id -> true

  const load = async () => setRows((await api.get("/suppliers")).data);
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, lead_time_days: Number(form.lead_time_days) || 7 };
    try {
      if (editing) await api.patch(`/suppliers/${editing}`, payload);
      else await api.post("/suppliers", payload);
      toast.success(editing ? "Lieferant aktualisiert" : "Lieferant angelegt");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const openCreate = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...empty, ...s }); setEditing(s.id); setOpen(true); };

  const removeSupplier = async (s) => {
    const ok = await confirm({
      title: "Lieferant löschen?",
      description: `„${s.name}" wird endgültig gelöscht. Zugewiesene Artikel verlieren ihre Lieferanten-Zuordnung. Bestellungen bleiben erhalten.`,
      confirmLabel: "Endgültig löschen",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/suppliers/${s.id}`);
      toast.success(`Lieferant „${s.name}" gelöscht`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen"); }
  };

  const openProducts = async (supplier) => {
    setProductSupplier(supplier);
    try {
      const { data } = await api.get(`/suppliers/${supplier.id}/products`);
      setAssignedItems(data.items || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const closeProducts = () => {
    setProductSupplier(null);
    setAssignedItems([]);
  };

  const openPicker = async () => {
    setPickerOpen(true);
    setPickerQuery("");
    setPickerSelected({});
    try {
      const { data } = await api.get("/items");
      setAllItems(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const availableItems = useMemo(() => {
    const assignedIds = new Set(assignedItems.map((i) => i.id));
    const s = pickerQuery.toLowerCase().trim();
    return allItems.filter((it) => {
      if (assignedIds.has(it.id)) return false;
      if (!s) return true;
      return (it.name || "").toLowerCase().includes(s)
          || (it.sku || "").toLowerCase().includes(s);
    });
  }, [allItems, assignedItems, pickerQuery]);

  const assignSelected = async () => {
    const ids = Object.keys(pickerSelected).filter((k) => pickerSelected[k]);
    if (!ids.length) { toast.error("Keine Artikel ausgewählt"); return; }
    try {
      await api.post(`/suppliers/${productSupplier.id}/products`, { item_ids: ids });
      toast.success(`${ids.length} Artikel zugeordnet`);
      const { data } = await api.get(`/suppliers/${productSupplier.id}/products`);
      setAssignedItems(data.items || []);
      setPickerOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const unassign = async (itemId) => {
    try {
      await api.delete(`/suppliers/${productSupplier.id}/products/${itemId}`);
      setAssignedItems((prev) => prev.filter((it) => it.id !== itemId));
      toast.success("Zuordnung entfernt");
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  return (
    <div data-testid="page-suppliers">
      <PageHeader
        eyebrow="Einkauf"
        title="Lieferanten"
        description="Mais-Bauer, Öl-Presser, Verpackungs-Partner – alle Zulieferer im Überblick."
        actions={
          <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-full" data-testid="btn-import-suppliers"
                  onClick={() => setImportOpen(true)}>
            <FileUp size={16} className="mr-1" /> Importieren
          </Button>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditing(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="btn-new-supplier" onClick={openCreate} className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"><Plus size={16} className="mr-1" /> Neuer Lieferant</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{editing ? "Lieferant bearbeiten" : "Neuer Lieferant"}</DialogTitle>
                <DialogDescription>Kontaktdaten und Standard-Lieferzeit für den Bestellvorschlag.</DialogDescription>
              </DialogHeader>
              <form onSubmit={save} className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Name *</Label><Input required data-testid="input-supplier-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Ansprechpartner</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
                <div><Label>Unsere Kundennummer</Label><Input data-testid="input-supplier-our-customer-number" placeholder="z.B. 10045" value={form.our_customer_number || ""} onChange={(e) => setForm({ ...form, our_customer_number: e.target.value })} /><div className="text-xs text-[var(--text-muted)] mt-1">Unsere Kundennummer beim Lieferanten (für Bestellungen).</div></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div className="col-span-2"><Label>Straße</Label><Input value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} /></div>
                <div><Label>PLZ</Label><Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} /></div>
                <div><Label>Ort</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                <div><Label>Land</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
                <div className="col-span-2"><Label>Lieferzeit (Tage)</Label><Input type="number" min="0" placeholder="7" data-testid="input-supplier-lead-time" value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })} /><div className="text-xs text-[var(--text-muted)] mt-1">Wie lange dauert eine Bestellung normalerweise? Wird für den Bestellvorschlag benötigt.</div></div>
                <DialogFooter className="col-span-2">
                  <Button type="submit" data-testid="btn-save-supplier" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">{editing ? "Änderungen speichern" : "Speichern"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />
      <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
        <Table className="table-zebra">
          <TableHeader>
            <TableRow>
              <TableHead className="overline">Name</TableHead>
              <TableHead className="overline">Ansprechpartner</TableHead>
              <TableHead className="overline">Email</TableHead>
              <TableHead className="overline">Ort</TableHead>
              <TableHead className="overline">Land</TableHead>
              <TableHead className="overline text-right">Lieferzeit</TableHead>
              <TableHead className="overline text-right">Produkte</TableHead>
              <TableHead className="overline text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`supplier-row-${r.id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell>{r.contact_person || "—"}</TableCell>
                <TableCell className="text-sm">{r.email || "—"}</TableCell>
                <TableCell>{r.city || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{r.country}</TableCell>
                <TableCell className="text-right font-mono text-xs">{r.lead_time_days ?? 7} T</TableCell>
                <TableCell className="text-right">
                  <button
                    onClick={() => openProducts(r)}
                    data-testid={`btn-supplier-products-${r.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--pop-pink)] hover:underline"
                  >
                    <Package size={14} /> Produkte
                  </button>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button onClick={() => openEdit(r)} data-testid={`btn-edit-supplier-${r.id}`}
                          className="text-xs font-semibold hover:text-[var(--pop-caramel)] mr-3 inline-flex items-center gap-1">
                    <Pencil size={12} /> Bearbeiten
                  </button>
                  <button onClick={() => removeSupplier(r)} data-testid={`btn-delete-supplier-${r.id}`}
                          title="Lieferant löschen"
                          className="text-xs font-semibold text-red-600 hover:text-red-700 inline-flex items-center gap-1">
                    <Trash2 size={12} /> Löschen
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-[var(--text-muted)]">Keine Lieferanten.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Products drawer */}
      <Dialog open={!!productSupplier} onOpenChange={(o) => !o && closeProducts()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Produkte – {productSupplier?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="flex justify-between items-center mb-2">
            <div className="text-sm text-[var(--text-muted)]">
              {assignedItems.length} zugewiesene Artikel
            </div>
            <Button
              onClick={openPicker}
              data-testid="btn-assign-products"
              className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"
            >
              <Plus size={16} className="mr-1" /> Artikel zuordnen
            </Button>
          </div>

          <div className="border border-[var(--border)] rounded-xl overflow-hidden max-h-[420px] overflow-y-auto">
            <Table className="table-zebra">
              <TableHeader>
                <TableRow>
                  <TableHead className="overline">SKU</TableHead>
                  <TableHead className="overline">Name</TableHead>
                  <TableHead className="overline">Typ</TableHead>
                  <TableHead className="overline text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignedItems.map((it) => (
                  <TableRow key={it.id} data-testid={`assigned-item-${it.id}`}>
                    <TableCell className="font-mono text-xs">{it.sku}</TableCell>
                    <TableCell>{it.name}</TableCell>
                    <TableCell className="text-xs">{it.type}</TableCell>
                    <TableCell className="text-right">
                      <button
                        onClick={() => unassign(it.id)}
                        data-testid={`btn-unassign-${it.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--danger)] hover:underline"
                      >
                        <X size={14} /> entfernen
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
                {assignedItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-[var(--text-muted)]">
                      Noch keine Artikel zugeordnet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Picker */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Artikel auswählen</DialogTitle>
          </DialogHeader>

          <div className="relative mb-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input
              placeholder="Suche SKU oder Name…"
              value={pickerQuery}
              onChange={(e) => setPickerQuery(e.target.value)}
              className="pl-8 rounded-xl h-9"
              data-testid="input-picker-search"
            />
          </div>

          <div className="border border-[var(--border)] rounded-xl overflow-hidden max-h-[380px] overflow-y-auto">
            <Table className="table-zebra">
              <TableHeader>
                <TableRow>
                  <TableHead className="overline w-10"></TableHead>
                  <TableHead className="overline">SKU</TableHead>
                  <TableHead className="overline">Name</TableHead>
                  <TableHead className="overline">Typ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {availableItems.map((it) => {
                  const checked = !!pickerSelected[it.id];
                  return (
                    <TableRow
                      key={it.id}
                      data-testid={`picker-row-${it.id}`}
                      onClick={() => setPickerSelected((s) => ({ ...s, [it.id]: !s[it.id] }))}
                      className="cursor-pointer"
                    >
                      <TableCell>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${checked ? "bg-[var(--pop-pink)] border-[var(--pop-pink)]" : "border-[var(--border)]"}`}>
                          {checked && <Check size={14} className="text-white" />}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{it.sku}</TableCell>
                      <TableCell>{it.name}</TableCell>
                      <TableCell className="text-xs">{it.type}</TableCell>
                    </TableRow>
                  );
                })}
                {availableItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-[var(--text-muted)]">
                      Keine passenden Artikel.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPickerOpen(false)} className="rounded-full">Abbrechen</Button>
            <Button
              onClick={assignSelected}
              data-testid="btn-confirm-assign"
              className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"
            >
              Zuordnen ({Object.values(pickerSelected).filter(Boolean).length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <ImportDialog open={importOpen} onOpenChange={setImportOpen}
        entityLabel="Lieferanten"
        previewEndpoint="/suppliers/import/preview"
        commitEndpoint="/suppliers/import/commit"
        templateUrl={`${API_BASE}/suppliers/import/template.xlsx`}
        previewKey="rows"
        onDone={() => load()}
        renderPreviewHeader={() => (<>
          <th className="text-left p-2">Zeile</th>
          <th className="text-left p-2">Name</th>
          <th className="text-left p-2">Kontakt</th>
          <th className="text-left p-2">Ort</th>
          <th className="text-left p-2">Aktion</th>
        </>)}
        renderPreviewRow={(row, i) => {
          const b = row.action === "create" ? "bg-green-100 text-green-700"
                  : row.action === "update" ? "bg-blue-100 text-blue-700"
                  : "bg-red-100 text-red-700";
          return (
            <tr key={i} className="border-t border-[var(--border)]">
              <td className="p-2 text-[var(--text-muted)] font-mono">{row._row}</td>
              <td className="p-2 font-semibold">{row.name}</td>
              <td className="p-2 text-xs">{[row.contact, row.email].filter(Boolean).join(" · ")}</td>
              <td className="p-2 text-xs">{[row.zip, row.city].filter(Boolean).join(" ")}</td>
              <td className="p-2"><span className={`pop-pill text-[10px] font-bold ${b}`}>{row.action}</span></td>
            </tr>
          );
        }} />
    </div>
  );
}
